/**
 * @module ui/templates/election/riding-card
 */
import tgamConfig from "config/tgam";

/**
 * Riding card template
 * @param {Object} r - Riding data
 * @param {String} r.imgDir - SVG image directory name
 * @param {Number} r.id - Unique riding ID
 * @param {Boolean} r.key - Whether this is a "key" riding
 * @param {String} r.name - Riding name
 * @param {Boolean} r.expanded - Whether to display the card in "expanded" or "collapsed" view
 * @param {String} r.url - URL that the riding card should link to
 * @param {Number} r.totalVoters - Total voters in the riding
 * @param {Number} r.totalPolls - Total polls in the riding
 * @param {Number} r.reportedPolls - The number of reported polls in the riding
 * @param {String} r.pollStatus - The current status of the polls (i.e. an empty string, "partial" or "complete")
 * @param {Array} r.votesByParty - The percentage of counted votes that each party has received
 * @param {Object} r.candidate - Information about the selected candidate
 * @param {Boolean} r.candidateIsIncumbent - Whether the selected candidate is the incumbent
 * @returns {String} HTML markup representing a single riding card
 */
export default function ridingCardTemplate(r) {
  const c = r.candidate;
  const statusClass = r.pollStatus ? ` c-election-riding-card--${r.pollStatus}` : "";
  const expandedClass = r.expanded ? " c-election-riding-card--expanded" : "";
  const cardAttributes = `class="c-election-riding-card c-election-riding-card--${c.partyClassName}${statusClass}${expandedClass}" data-riding-id="${r.id}"`;

  if (r.expanded) {
    const percentOfPollsReported = Math.round(r.reportedPolls / r.totalPolls * 100);

    let rainbow = "";
    if (r.votesByParty) {
      const rainbowRows = r.votesByParty.map(item => {
        return `<div class="c-election-riding-card__rainbow__${item.partyClassName}" style="width:${item.votePercentage}%"></div>`;
      });
      rainbow = `
        <div class="c-election-riding-card__rainbow" aria-hidden="true">
          ${rainbowRows.join("")}
        </div>
      `;
    }
    const svgPath = `${tgamConfig.env.staticMediaUrl}/assets/panther/images/election/${r.imgDir}/${r.id}.svg#riding-${r.id}`;
    return `
      <div ${cardAttributes}>
        <div class="c-election-riding-card__inner">
          <div class="c-election-riding-card__shape">
            <svg class="c-election-riding-card__art" role="img" pointer-events="none" focusable="false" aria-hidden="true">
              <use xlink:href="${svgPath}"></use>
            </svg>
          </div>
          <div class="c-election-riding-card__content">
            <div class="c-election-riding-card__name">${r.name}</div>
            <div class="c-election-riding-card__results">${percentOfPollsReported}% Polls in: <span class="c-election-riding-card__polls">${r.reportedPolls}/${r.totalPolls}</span>
            </div>
          </div>
        </div>
      </div>
      ${rainbow}
    `;
  } else {
    const incumbentIcon = `<span class="c-indicator-icon c-indicator-icon--rerun">
      <svg class="c-indicator-icon__art" role="img" pointer-events="none" focusable="false" aria-hidden="true">
        <use xlink:href="${tgamConfig.env.svgSpritePath}#icon-rerun"></use>
      </svg>
      <span class="c-election-riding-card__indicator-icon-screenreader-text u-visually-hidden">Incumbent</span>
    </span>`;

    const partyContentInfo = `
      ${c.partyAbbrName} <span class="c-election-riding-card__party-candidate">${c.name}${r.candidateIsIncumbent ? incumbentIcon : ""}</span>
    `;
    let partyContent = "";

    switch (r.pollStatus) {
      case "":
        partyContent = partyContentInfo;
        break;
      case "partial":
        partyContent = `
          <span class="c-election-riding-card__party__circle"></span> ${partyContentInfo}
        `;
        break;
      case "complete":
        partyContent = `
          <span class="c-indicator-icon">
            <svg class="c-indicator-icon__art" role="img" pointer-events="none" focusable="false" aria-hidden="true">
              <use xlink:href="${tgamConfig.env.svgSpritePath}#icon-checkmark"></use>
            </svg>
          </span> ${partyContentInfo}
        `;
        break;
    }
    return `
      <div ${cardAttributes}>
        <a href="${r.url}" class="c-election-riding-card__link" aria-haspopup="true" data-popup="js-modal-dialog-election-riding">
          <div class="c-election-riding-card__name">${r.name}</div>
          <div class="c-election-riding-card__party">${partyContent}</div>
          <div class="c-election-riding-card__percent">${c.percentageOfVotesWon}%</div>
          <div class="c-election-riding-card__key${r.key ? "" : "--hidden"}">
            <span class="c-indicator-icon c-election-key-riding__indicator-icon">
              <svg class="c-indicator-icon__art" role="img" pointer-events="none" focusable="false" aria-hidden="true">
                <use xlink:href="${tgamConfig.env.svgSpritePath}#icon-star"></use>
              </svg>
              <span class="c-election-key-riding__indicator-icon-screenreader-text u-visually-hidden">Key Riding</span>
            </span>
          </div>
        </a>
      </div>
    `;
  }
}
